/* =============================================================
   TOAST — the app's one notifier

   Replaces react-toastify, which was imported in 91 files, mounted
   25 separate <ToastContainer>s (so a message could land in any of
   them, or in several), and shipped its own stylesheet that no
   palette could reach.

   This is a plain store plus a subscriber. The API is deliberately
   the same shape the call sites already use -- toast.success /
   .error / .warning / .warn / .info, message first, options second
   -- so the 390-odd calls did not have to be rewritten, only
   re-pointed. Nothing here imports React: the host subscribes, and
   a service or a plain function can raise a toast without being in
   a component.
   ============================================================= */

/** How long each kind sits there, in ms. */
const DEFAULT_DURATION = {
  success: 4000,
  info: 5000,
  warning: 6000,
  // Longest, because it is the one the reader has to act on, and the
  // only one that says something went wrong.
  error: 7000,
};

const MAX_VISIBLE = 4;

let nextId = 1;
let items = [];
const listeners = new Set();

const emit = () => {
  for (const listener of listeners) listener(items);
};

export const subscribe = (listener) => {
  listeners.add(listener);
  listener(items);
  return () => listeners.delete(listener);
};

export const dismissToast = (id) => {
  const before = items.length;
  items = items.filter((t) => t.id !== id);
  if (items.length !== before) emit();
};

export const clearToasts = () => {
  if (!items.length) return;
  items = [];
  emit();
};

/**
 * react-toastify took an options object as the second argument and
 * pages passed it freely. `autoClose` is the only one that changed
 * anything worth keeping (a number of ms, or false to make it stay);
 * `position`, `hideProgressBar`, `closeOnClick`, `pauseOnHover`,
 * `draggable` and the rest described a container this app now has
 * exactly one of, so they are accepted and ignored rather than
 * making 390 call sites edit their options out.
 */
const push = (type, message, options = {}) => {
  const text = typeof message === 'string' ? message : String(message ?? '');
  if (!text.trim()) return null;

  const duration =
    options.autoClose === false
      ? 0
      : Number(options.autoClose) || DEFAULT_DURATION[type] || 5000;

  // A failing request in a loop, or one save that reports the same
  // problem from three places, used to stack three identical strips.
  // The same message already on screen counts up instead.
  const existing = items.find((t) => t.type === type && t.text === text);
  if (existing) {
    items = items.map((t) =>
      t.id === existing.id
        ? { ...t, count: t.count + 1, duration, seq: nextId++ }
        : t,
    );
    emit();
    return existing.id;
  }

  const id = nextId++;
  items = [...items, { id, type, text, duration, count: 1, seq: id }];
  // Oldest first: the newest message is the one being read.
  if (items.length > MAX_VISIBLE) items = items.slice(items.length - MAX_VISIBLE);
  emit();
  return id;
};

/** A new notification is read, not glanced at: title, summary, sender. */
const NOTIFICATION_DURATION = 9000;

/**
 * A notification that just arrived. Unlike the four kinds above it is a
 * way IN, not only a report: `onOpen` runs when the strip is pressed, so
 * the caller decides where that goes (the bell sends it to
 * /notifications). Kept separate from push() because it carries fields
 * the others do not, and because the same notification must never be
 * announced twice -- it is de-duplicated by the notification's own id,
 * not by its text, since two different notifications can share a title.
 *
 * @param {{ id?: string, title: string, summary?: string, source?: string,
 *           from?: string, priorityLabel?: string, priorityTone?: string,
 *           attendanceStatus?: 'present' | 'absent',
 *           onOpen?: Function, duration?: number }} payload
 */
const pushNotification = (payload = {}) => {
  const text = String(payload.title || '').trim();
  if (!text) return null;

  const key = payload.id ? `notification:${payload.id}` : null;
  if (key && items.some((t) => t.key === key)) return null;

  const id = nextId++;
  items = [
    ...items,
    {
      id,
      key,
      type: 'notification',
      text,
      summary: String(payload.summary || ''),
      source: String(payload.source || ''),
      from: String(payload.from || ''),
      priorityLabel: String(payload.priorityLabel || ''),
      priorityTone: ['high', 'medium', 'low'].includes(payload.priorityTone) ? payload.priorityTone : 'none',
      // Attendance only: how the student was marked. Colours the strip green
      // or red and outranks priority, which an attendance notice never sets.
      attendanceStatus: ['present', 'absent'].includes(payload.attendanceStatus) ? payload.attendanceStatus : null,
      onOpen: typeof payload.onOpen === 'function' ? payload.onOpen : null,
      duration: payload.duration === 0 ? 0 : Number(payload.duration) || NOTIFICATION_DURATION,
      count: 1,
      seq: id,
    },
  ];
  if (items.length > MAX_VISIBLE) items = items.slice(items.length - MAX_VISIBLE);
  emit();
  return id;
};

/* A popup that is open takes its own success message: the popup shows it
   in place of its content and closes a few seconds later (PopupDone),
   instead of a toast appearing beside it. Popups register while they are
   open (usePopupDone); the newest open one is the one a message is for.
   A sink returns false to decline, and the message is toasted as before.
   `{ toast: true }` in the options always toasts. */
const doneSinks = [];

export const registerDoneSink = (sink) => {
  doneSinks.push(sink);
  return () => {
    const i = doneSinks.lastIndexOf(sink);
    if (i !== -1) doneSinks.splice(i, 1);
  };
};

const success = (message, options) => {
  const text = typeof message === 'string' ? message : String(message ?? '');
  const sink = doneSinks[doneSinks.length - 1];
  if (sink && text.trim() && !options?.toast && sink(text) !== false) return null;
  return push('success', message, options);
};

export const toast = {
  notification: pushNotification,
  success,
  error: (message, options) => push('error', message, options),
  warning: (message, options) => push('warning', message, options),
  // react-toastify spelled it both ways and this project used both.
  warn: (message, options) => push('warning', message, options),
  info: (message, options) => push('info', message, options),
  dismiss: dismissToast,
  clear: clearToasts,
};

export default toast;
